import user from './user';
import { customLoggingLevels } from '../config/constants';
import { appConfig } from '../config/app.config';
import _ from 'lodash';
import moment from 'moment';

interface LoggerOptions {
    log_level: string;
    api_options?: any;
    disabled?: boolean;
    server_url?: string;
    max_batch_size?: number;
    enable_api_logs?: boolean;
    enable_console_logs?: boolean;
    max_flush_interval_sec?: number;
}

class RemoteLogger {
    private static instance: RemoteLogger;
    private logMessages: any[] = [];
    private log_level: string | number;
    private apiOptions: any;
    private disabled: boolean;
    private url: string;
    private batchSize: number;
    private getLogLevel: () => number;
    private apiLogsEnabled: boolean;
    private consoleLogsEnabled: boolean;
    private flushInterval: number;
    private genericFields: any;
    private timerInstance: ReturnType<typeof setInterval> | null = null;

    static get LOG() {
        return customLoggingLevels.LOG;
    }
    static get INFO() {
        return customLoggingLevels.INFO;
    }
    static get WARN() {
        return customLoggingLevels.WARN;
    }
    static get ERROR() {
        return customLoggingLevels.ERROR;
    }
    static get DEBUG() {
        return customLoggingLevels.DEBUG;
    }
    static get NONE() {
        return customLoggingLevels.NONE;
    }

    constructor(options: LoggerOptions) {
        if (RemoteLogger.instance) {
            return RemoteLogger.instance;
        }
        RemoteLogger.instance = this;
        if (!options || Object.prototype.toString.call(options).indexOf('Object') === -1) {
            throw new Error('provide options object to enable logger');
        }
        this.init(options);
    }

    /**
     * Initialize the logger with options.
     * @param options - The logger options.
     */
    init(options: LoggerOptions) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        this.log_level = options.log_level in customLoggingLevels ? options.log_level : RemoteLogger.ERROR;
        this.apiOptions = options.api_options || {};
        this.disabled = options.disabled || false;
        this.url = options.server_url || '';
        this.batchSize = options.max_batch_size || 50;
        this.getLogLevel = () => RemoteLogger[this.log_level] || RemoteLogger.ERROR;
        this.apiLogsEnabled = options.enable_api_logs || false;
        this.consoleLogsEnabled = options.enable_console_logs || false;
        this.flushInterval = options.max_flush_interval_sec || 300;
        this.genericFields = {
            tenant_id: 'NA',
            app_version: 'NA',
            user_id: user.isUserLoggedIn() ? user.getUsername() : 'NA',
            vmx_id: user.isUserLoggedIn() ? user.getUserDeviceId() : 'NA',
            device_id: user.isUserLoggedIn() ? user.getUserDeviceId() : 'NA',
        };
        const flushIntervalInMilli = this.flushInterval * 1000;
        if (this.timerInstance) {
            clearInterval(this.timerInstance);
        }
        if (!this.disabled) {
            this.timerInstance = setInterval(this.send.bind(this), flushIntervalInMilli);
        }
        // this._onUnloadAttachListener();
        // this._onErrorAttachListener();
    }

    /**
     * Capture global errors.
     * @param event - The error event.
     */
    _captureGlobalErrors(event: ErrorEvent) {
        const { error, message, filename, lineno, colno } = event || {};
        const { stack } = error || {};
        const msg = ['Message: ' + message, 'Filename: ' + filename, 'Line: ' + lineno, 'Column: ' + colno].join(' - ');

        if (stack) {
            this.error(`Message: ${msg}, Stack: ${JSON.stringify(stack)}`, 'global');
        } else if (error) {
            this.error(`Message: ${msg}, Error: ${JSON.stringify(error)}`, 'global');
        }
    }

    /**
     * Attach global error listener.
     */
    _onErrorAttachListener() {
        window.addEventListener('error', this._captureGlobalErrors.bind(this));
    }

    /**
     * Attach unload listener.
     */
    // _onUnloadAttachListener() {
    //     if (window.addEventListener) {
    //         window.addEventListener('beforeunload', this._onUnload.bind(this), false);
    //         // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //     } else if (window.attachEvent) {
    //         // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //         window.attachEvent('onbeforeunload', this._onUnload.bind(this));
    //     }
    // }

    /**
     * Handle unload event.
     */
    _onUnload() {
        //keep-alive should be true in case of unload event.
        this.send(true);
    }

    /**
     * Process log message.
     * @param message - The log message.
     * @param level - The log level.
     */
    _processLog(message: string, level?: number) {
        if (this.logMessages.length >= this.batchSize) {
            if (!this.disabled) {
                this.send();
            }
        }
        this.logMessages.push({
            message,
            level,
        });
    }

    /**
     * Filter log messages.
     * @returns Filtered log messages.
     */
    _filter() {
        let messages = this.logMessages || [];
        if (!messages.length) return messages;
        messages = messages.filter((item) => {
            return typeof item.level === 'undefined' ? (this.apiLogsEnabled ? true : false) : true;
        });
        messages = messages.filter((item) => {
            return item.level >= 0 ? (this.consoleLogsEnabled ? true : false) : true;
        });
        return messages;
    }

    /**
     * Parse console log message.
     * @param message - The log message.
     * @param level - The log level.
     * @param tag - The log tag.
     * @returns Parsed log message.
     */
    _parseConsole(message = '', level = 0, tag = '') {
        return Object.assign({}, this.genericFields, {
            priority: this._getPriorityString(level),
            tag,
            message,
            timestamp: this._getUTCTime(),
            type: 'CONSOLE',
        });
    }

    /**
     * Stringify console log message.
     * @param params - The log parameters.
     * @param format - The log format.
     * @returns Stringified log message.
     */
    _stringifyConsole(params: any, format = 'tenant_id|app_version|user_id|vmx_id|device_id|priority|tag|message') {
        if (!params) return '';
        const keys = format.split('|');
        let string = '';
        string += `${params.timestamp}|${params.type}`;
        for (let i = 0; i < keys.length; i++) {
            string += `|${keys[i]}:${JSON.stringify(params[keys[i]])}`;
        }
        return string || '';
    }

    /**
     * Parse player error log message.
     * @param message - The log message.
     * @param level - The log level.
     * @param tag - The log tag.
     * @returns Parsed log message.
     */
    _parsePlayerError(message = '', level = 0, tag = '') {
        return Object.assign({}, this.genericFields, {
            priority: this._getPriorityString(level),
            tag,
            message,
            timestamp: this._getUTCTime(),
            type: 'PLAYER',
        });
    }

    /**
     * Stringify player error log message.
     * @param params - The log parameters.
     * @param format - The log format.
     * @returns Stringified log message.
     */
    _stringifyPlayerError(params: any, format = 'tenant_id|app_version|user_id|vmx_id|device_id|priority|tag|message') {
        if (!params) return '';
        const keys = format.split('|');
        let string = '';
        string += `${params.timestamp}|${params.type}`;
        for (let i = 0; i < keys.length; i++) {
            string += `|${keys[i]}:${JSON.stringify(params[keys[i]])}`;
        }
        return string || '';
    }

    /**
     * Parse API log message.
     * @param params - The log parameters.
     * @returns Parsed log message.
     */
    _parseApi(params: any) {
        if (!params) return {};
        switch (params.type) {
            case 'REQUEST':
                return Object.assign({}, this.genericFields, {
                    type: 'API',
                    request_type: params.type || '',
                    request_url: params.url || '',
                    http_method: params.method || '',
                    request_headers: params.headers || '',
                    request_body: params.body || '',
                    timestamp: this._getUTCTime(),
                });
            case 'RESPONSE':
                return Object.assign({}, this.genericFields, {
                    type: 'API',
                    request_type: params.type || '',
                    http_response_code: params.status || '',
                    timestamp: this._getUTCTime(),
                    value: params.value || '',
                    error: params.error || '',
                });
            default:
                return {};
        }
    }

    /**
     * Validate if response is successful.
     * @param responseCode - The response code.
     * @returns True if response is successful, false otherwise.
     */
    _validateResponseIsSuccess(responseCode: number) {
        if (!responseCode) return;
        if (responseCode >= 200 && responseCode <= 299) {
            return true;
        }
        if (responseCode >= 300 && responseCode <= 399) {
            return true;
        }
        return false;
    }

    /**
     * Stringify API log message.
     * @param params - The log parameters.
     * @returns Stringified log message.
     */
    _stringifyApi(params: any) {
        if (!params) return '';
        let format = '';
        let string = '';
        string += `${params.timestamp}|${params.type}`;
        switch (params.request_type) {
            case 'REQUEST':
                format =
                    'tenant_id|app_version|user_id|vmx_id|device_id|http_method|request_url|request_headers|request_body';
                break;
            case 'RESPONSE':
                format = 'tenant_id|app_version|user_id|vmx_id|device_id|http_response_code|value|error';
                break;
        }
        const keys = format.split('|');
        for (let i = 0; i < keys.length; i++) {
            string += `|${keys[i]}:${JSON.stringify(params[keys[i]])}`;
        }
        return string || '';
    }

    /**
     * Log console message.
     * @param message - The log message.
     * @param level - The log level.
     * @param tag - The log tag.
     */
    _console(message: any, level: number, tag = '') {
        if (!message) {
            return;
        }
        if (Object.prototype.toString.call(message) === '[object Object]' && _.isEmpty(message)) {
            return;
        }
        //Stringify if object before logging
        if (Object.prototype.toString.call(message) !== '[object String]') {
            message = JSON.stringify(message);
        }
        if (message.replace(/ /g, '') === '{}') return;
        if (this.getLogLevel() === RemoteLogger.NONE) return;
        if (level >= this.getLogLevel()) {
            const parsed = this._parseConsole(message, level, tag);
            const string = this._stringifyConsole(parsed);
            this._processLog(string, level);
        }
    }

    /**
     * Log player error message.
     * @param message - The log message.
     * @param level - The log level.
     * @param tag - The log tag.
     */
    _player(message: any, level: number, tag = '') {
        if (!message) {
            return;
        }
        //Stringify if object before logging
        if (typeof message === 'object') {
            message = JSON.stringify(message);
        }
        if (this.getLogLevel() === RemoteLogger.NONE) return;
        if (level >= this.getLogLevel()) {
            const parsed = this._parsePlayerError(message, level, tag);
            const string = this._stringifyPlayerError(parsed);
            this._processLog(string, level);
        }
    }

    /**
     * Clear log messages.
     */
    _clearLogs() {
        this.logMessages = [];
    }

    /**
     * Get priority string.
     * @param priority - The priority level.
     * @returns Priority string.
     */
    _getPriorityString(priority = -1) {
        for (const key in customLoggingLevels) {
            if (customLoggingLevels[key] === priority) {
                return key.toUpperCase();
            }
        }
        return '';
    }

    /**
     * Get UTC time.
     * @returns UTC time string.
     */
    _getUTCTime() {
        return moment().utc().format('DD-MM-YYYY hh:mm:ss');
    }

    /**
     * Send log messages to server.
     * @param keepalive - Whether to keep the connection alive.
     */
    send(keepalive = false) {
        const { device_id: deviceID, vmx_id: vmxId, user_id: accountID } = this.genericFields || {};
        const logs = this._filter() || [];
        this._clearLogs();
        if (!logs.length) {
            return;
        }

        const data = logs.map((item) => item.message).join('$\n');
        const postLogs = (async () => {
            let response;
            try {
                response = await fetch(this.url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'text/html',
                        identifier: `${deviceID}-${vmxId}-${accountID}`,
                    },
                    keepalive: keepalive || false,
                    body: data,
                });
            } catch (error) {
                this.error(error);
            }
            return response?.json() || {};
        })();

        //postLogs.catch((error) => { });
    }

    /**
     * Log API messages.
     * @param params - The API log parameters.
     */
    api(params: any[] = []) {
        const [req, resp] = params || [];
        if (!req || !resp) return;
        const responseStatus = resp.status;
        if (this.apiOptions.errors_only) {
            if (this._validateResponseIsSuccess(responseStatus)) return;
        }
        params.forEach((log) => {
            const json = this._parseApi(log);
            const msg = this._stringifyApi(json);
            this._processLog(msg);
        });
    }

    /**
     * Log player error message.
     * @param message - The log message.
     * @param tag - The log tag.
     */
    player(message: any, tag = '') {
        this._player(message, RemoteLogger.ERROR, tag);
    }

    /**
     * Log message.
     * @param message - The log message.
     * @param tag - The log tag.
     */
    log(message: any, tag = '') {
        this._console(message, RemoteLogger.LOG, tag);
    }

    /**
     * Log error message.
     * @param message - The log message.
     * @param tag - The log tag.
     */
    error(message: any, tag = '') {
        this._console(message, RemoteLogger.ERROR, tag);
    }

    /**
     * Log warning message.
     * @param message - The log message.
     * @param tag - The log tag.
     */
    warn(message: any, tag = '') {
        this._console(message, RemoteLogger.WARN, tag);
    }

    /**
     * Log info message.
     * @param message - The log message.
     * @param tag - The log tag.
     */
    info(message: any, tag = '') {
        this._console(message, RemoteLogger.INFO, tag);
    }

    /**
     * Log debug message.
     * @param message - The log message.
     * @param tag - The log tag.
     */
    debug(message: any, tag = '') {
        this._console(message, RemoteLogger.DEBUG, tag);
    }
}

export default () => {
    if (!appConfig.remoteLogger || appConfig.remoteLogger.disabled) {
        return;
    }
    return new RemoteLogger(appConfig.remoteLogger);
};
